import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRightSideBar } from '@p81-common/ui';

import isEqual from 'lodash/isEqual';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import {
  EmptyState,
  ExportButton,
  FilterButton,
  LimitPaginationButton,
  Loading,
  PageTitle,
  PaginationGroup,
  SaveFilterBlock,
  TableColumnSetting,
  TableTitle
} from 'components/common/Elements/Elements';
import AllTenantsTable from './parts/AllTenantsTable/AllTenantsTable';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';

import { AllTenantsReq } from './helpers/helpers';
import { validationNumber } from 'utils/validation';
import { getRequest, postRequest } from 'utils/requests';
import {
  replaceHistory,
  paddingForMenuSettings,
  checkingForExistenceOfOptions,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters
} from 'utils/common';
import {
  exportConfig,
  defaultFilters,
  defaultNavigation,
  mainTableConf,
  FILTER_COLUMN_DEPENDENCIES
} from './helpers/configurates';

import { useTheme } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import classes from './AllTenants.module.scss';
import { hasAccess, resources, access } from 'utils/permissions';

const AllTenants = () => {
  const { t } = useTranslation(['activity']);

  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });
  const [filterBackend, setFilterBackend] = useState({
    key: 'accountsFilters',
    value: []
  });
  const [visibleFilterSave, setVisibleFilterSave] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [triggerResetDatePicker, setTriggerResetDatePicker] = useState(false);

  //filters and pagination
  const [filterButton, setFilterButton] = useState(false);
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(mainTableConf))
  );

  //data from api
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({});

  //other data
  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(false);
  const [dataConnection, setDataConnection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  const [startFilter, setStartFilter] = useState(false);
  const [prevStateFilter, setPrevStateFilter] = useState();
  const [prevStateTableConf, setPrevStateTableConf] = useState();

  const [triggerSaveTableSettings, setTriggerSaveTableSettings] =
    useState(false);
  const [triggerStartEffect, setTriggerStartEffect] = useState(false);
  const [enabledRequest, setEnabledRequest] = useState(true);
  const [prevStartFilter, setPrevStartFilter] = useState(startFilter);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const tableHeight = useRef();
  const unmount = useRef(false);

  // Helper to auto-show/hide columns based on filter states
  // Uses FILTER_COLUMN_DEPENDENCIES configuration to determine which columns
  // should appear when their corresponding filters have non-default values
  //
  // @param {Array} tableConfig - Current table configuration
  // @param {Object} currentFilter - Current filter state
  // @param {Object} dependencies - Filter-column dependency mapping (optional, defaults to FILTER_COLUMN_DEPENDENCIES)
  // @returns {Array} Updated table configuration with adjusted column visibility
  const applyAutoShowColumnRules = (
    tableConfig,
    currentFilter,
    dependencies = FILTER_COLUMN_DEPENDENCIES
  ) => {
    let updatedConfig = [...tableConfig];
    let hasChanges = false;

    // Iterate through all configured filter-column dependencies
    Object.entries(dependencies).forEach(([filterName, config]) => {
      const { columnField, showWhenFilterHasValue } = config;

      // Check if filter has non-default value
      const hasNonDefaultValue =
        Array.isArray(currentFilter[filterName]) &&
        currentFilter[filterName].length > 0;

      // Find the column in table config
      const columnIndex = updatedConfig.findIndex(
        col => col.field === columnField
      );

      if (columnIndex === -1) {
        console.warn(
          `Auto-show column '${columnField}' not found in table config for filter '${filterName}'`
        );
        return; // Skip this dependency
      }

      const shouldBeVisible = showWhenFilterHasValue && hasNonDefaultValue;
      const currentlyVisible = updatedConfig[columnIndex].show;

      // Only update if visibility needs to change
      if (currentlyVisible !== shouldBeVisible) {
        updatedConfig = [...updatedConfig];
        updatedConfig[columnIndex] = {
          ...updatedConfig[columnIndex],
          show: shouldBeVisible
        };
        hasChanges = true;
      }
    });

    return hasChanges ? updatedConfig : tableConfig;
  };

  const handleInput = e => {
    setFilter({ ...filter, companyName: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.companyName, value =>
    handleInput({ target: { value } })
  );

  const handleFromTo = e => {
    const target = e.currentTarget;
    const name = target.getAttribute('field');
    if (target.id === name) {
      setFilter({
        ...filter,
        [name]: {
          ...filter[name],
          [`from`]: '',
          [`to`]: ''
        }
      });
      setStartFilter(!startFilter);
    } else {
      if (!validationNumber(target.value)) {
        setFilter({
          ...filter,
          [name]: {
            ...filter[name],
            [target.name]: target.value
          }
        });
        setStartFilter(!startFilter);
      }
    }
  };

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };
  const handleLimitChange = limit => {
    if (filter.itemsPerPage === limit) {
      return;
    }
    setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
    setStartFilter(!startFilter);
  };
  const handleSortable = field => () => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = e => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  const onClearSearch = () => {
    setFilter({ ...filter, companyName: '' });
    setStartFilter(!startFilter);
  };

  const getFilterOptionsFromBackend = listFilter => {
    setSelectLoader(true);
    getRequest('/customers/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({ ...filters, ...body });
          // we are checking or in the new filter lists there are
          // parameters that are selected, and those that are not, turn off the selection
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              setEnabledRequest,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  useEffect(() => {
    //after the select is closed, we get into this function.
    //We check or after closing the filter whether there have
    //been changes in the selected options and if so, we make a request

    if (triggerStartEffect) {
      // Detect if this effect run was triggered by startFilter change (dropdown close)
      // vs tableConf change (manual column visibility toggle)
      const startFilterChanged = prevStartFilter !== startFilter;

      // Apply auto-show/hide rules when dropdown closes (startFilter changed)
      // Function handles both showing and hiding based on filter state
      const tableConfWithAutoShow = startFilterChanged
        ? applyAutoShowColumnRules(tableConf, filter)
        : tableConf;

      if (
        (!isEqual(prevStateFilter, filter) &&
          JSON.parse(query.get('filter')) !== null) ||
        !isEqual(
          parsConfigForHistory(prevStateTableConf),
          parsConfigForHistory(tableConfWithAutoShow)
        )
      ) {
        // If auto-show changed the config, update local state before saving
        if (tableConfWithAutoShow !== tableConf) {
          setTableConf(tableConfWithAutoShow);
          setPrevStateTableConf(tableConfWithAutoShow);
        }

        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          false,
          false,
          changeFilter,
          parsConfigForHistory(tableConfWithAutoShow)
        );
      }

      // Update prevStartFilter for next comparison
      setPrevStartFilter(startFilter);
    }
    setTriggerStartEffect(true);
    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    if (enabledRequest) {
      setLoaderTable(true);

      if (
        query.get('filter') === null &&
        isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        requestForFilteredData();
      } else if (
        query.get('filter') === null &&
        !isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        resetAllFilters(true);
        replaceHistory(
          filter,
          setEnabledRequest,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      } else {
        try {
          const parsedFilter = JSON.parse(query.get('filter'));
          setFilter(parsedFilter);
          setPrevStateFilter(parsedFilter);

          // Restore table config from URL
          let restoredTableConf = reParsConfigForHistory(
            JSON.parse(query.get('tableConf')),
            mainTableConf
          );

          // Apply auto-show rules based on restored filter
          restoredTableConf = applyAutoShowColumnRules(
            restoredTableConf,
            parsedFilter
          );

          // Update state with auto-shown config
          setPrevStateTableConf(restoredTableConf);
          setTableConf(restoredTableConf);

          setChangeFilter(query.get('changeFilter'));
          requestForFilteredData(parsedFilter, mainTableConf);
        } catch (error) {
          console.error(
            'Failed to parse URL parameters, resetting to defaults:',
            error
          );
          resetAllFilters(true);
          replaceHistory(
            filter,
            setEnabledRequest,
            history,
            'replace',
            {
              ...defaultFilters,
              ...defaultNavigation
            },
            'Default',
            mainTableConf
          );
          setVisibleFilterEdit(false);
          setVisibleFilterSave(false);
        }
      }
      setTriggerResetDatePicker(!triggerResetDatePicker);
    }
    setEnabledRequest(true);

    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/customers/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;

    const REQ_BODY = AllTenantsReq(listFilter);

    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setData(res.body);
          setDataConnection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConnection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };

  const resetAllFilters = (
    reload,
    onlyFilterLabel,
    onClickDefault,
    modeClearAll
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    }

    setTriggerResetDatePicker(!triggerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  const theme = useTheme();

  const isMdUp = useMediaQuery(theme.breakpoints.up('custommd'));
  const isSmUp = useMediaQuery(theme.breakpoints.down(1440));

  const {
    displayDifComponent,
    setDrawerBehavior,
    setShowDrawer,
    setOpen,
    setDrawerLocked
  } = useRightSideBar();

  const membersInfoDrawerContent = useMemo(
    () => (
      <Suspense fallback={<Loading />}>
        <span>Hello 2</span>
      </Suspense>
    ),
    []
  );

  useEffect(() => {
    return () => {
      unmount.current = true;
      setShowDrawer(false);
      setOpen(false);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setShowDrawer(false); // false to not show
    setOpen(false); // false to not show
    displayDifComponent(membersInfoDrawerContent);
    setDrawerBehavior();
    if (isSmUp) {
      setDrawerLocked(false);
      setOpen(false);
    }
    // eslint-disable-next-line
  }, [isMdUp, isSmUp]);

  if (loader) {
    return <Loading />;
  }

  if (!dataConnection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <PageTitle title={t('activity:customers:ALL_TENANTS')} />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.COMPANY_NAME_ID_PLACEHOLDER')}
          value={filter.companyName}
          onChange={handleInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />

        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllFilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={triggerResetDatePicker}
          setTrigerResetDatePicker={setTriggerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
          tableConf={tableConf}
          defaulTableConf={mainTableConf}
          setTableConf={setTableConf}
          triggerSaveTableSettings={triggerSaveTableSettings}
        />
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton}
        trigerResetDatePicker={triggerResetDatePicker}
        resetAllfilters={resetAllFilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      <div
        className={cn(
          classes.actions,
          !filterButton && classes.actions_paddingWhenFilterClose
        )}
      >
        <TableTitle
          title={t('activity:customers:ALL_TENANTS')}
          additional={data.totalRecords}
        />
        <div className={classes.actions_right}>
          <TableColumnSetting
            setFilterButton={setFilterButton}
            tableConf={tableConf}
            setTableConf={setTableConf}
            setVisibleFilterSave={setVisibleFilterSave}
            setVisibleFilterEdit={setVisibleFilterEdit}
            changeFilter={changeFilter}
            visibleFilterSave={visibleFilterSave}
            visibleFilterEdit={visibleFilterEdit}
            setTriggerSaveTableSettings={setTriggerSaveTableSettings}
            triggerSaveTableSettings={triggerSaveTableSettings}
            defaulTableConf={mainTableConf}
          />
          {hasAccess([resources.CUSTOMERS, access.EXPORT]) && (
            <ExportButton
              exportConfig={exportConfig}
              changeOptions={tableConf}
              isDisabled={!data.totalRecords}
            />
          )}
          <LimitPaginationButton
            currentLimit={filter.itemsPerPage}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      <div style={paddingForMenuSettings(tableConf, tableHeight)}>
        <div ref={tableHeight}>
          <AllTenantsTable
            tableData={data.data}
            handleSortable={handleSortable}
            filter={filter}
            loader={loaderTable}
            tableConf={tableConf}
            handleFromTo={handleFromTo}
            requestForFilteredData={requestForFilteredData}
          />
        </div>

        {data.data && loaderTable === false && data.totalPages > 1 && (
          <PaginationGroup
            pageCount={data.totalPages}
            setPage={changeCurrentPage}
            currentPage={filter.currentPage}
            itemsTotal={data.totalRecords}
            itemsPerPage={filter.itemsPerPage}
          />
        )}
      </div>
    </>
  );
};

AllTenants.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired
  }).isRequired,
  history: PropTypes.shape({ replace: PropTypes.func.isRequired }).isRequired
};

export default AllTenants;
